/**
 * Progression Tracking Types
 * 
 * Defines the stages and states for tracking video generation progression
 * from image upload through video completion.
 */

export type GenerationStage =
  | 'idle'
  | 'uploading_image'
  | 'storing_image_s3'
  | 'storing_image_db'
  | 'enhancing_prompt'
  | 'starting_generation'
  | 'generating_video'
  | 'storing_video_s3'
  | 'storing_video_db'
  | 'completed'
  | 'failed';

export interface StageProgress {
  stage: GenerationStage;
  progress: number; // 0-100
  message: string;
  timestamp: number;
}

export interface GenerationProgression {
  currentStage: GenerationStage;
  overallProgress: number; // 0-100
  currentStageProgress: number; // 0-100 within current stage
  message: string;
  stages: {
    [key in GenerationStage]?: {
      started: boolean;
      completed: boolean;
      progress: number;
      message?: string;
      timestamp?: number;
    };
  };
  error?: string;
  startedAt?: number;
  completedAt?: number;
}

/**
 * Stage configuration with progress ranges and messages
 */
export const STAGE_CONFIG: Record<
  GenerationStage,
  { minProgress: number; maxProgress: number; defaultMessage: string }
> = {
  idle: { minProgress: 0, maxProgress: 0, defaultMessage: 'Ready to start' },
  uploading_image: {
    minProgress: 0,
    maxProgress: 15,
    defaultMessage: 'Uploading image...',
  },
  storing_image_s3: {
    minProgress: 15,
    maxProgress: 25,
    defaultMessage: 'Storing image in S3...',
  },
  storing_image_db: {
    minProgress: 25,
    maxProgress: 30,
    defaultMessage: 'Saving image record...',
  },
  enhancing_prompt: {
    minProgress: 0,
    maxProgress: 10, // Small range - should complete in ~15 seconds
    defaultMessage: 'Enhancing prompt with AI...',
  },
  starting_generation: {
    minProgress: 10,
    maxProgress: 15,
    defaultMessage: 'Starting video generation...',
  },
  generating_video: {
    minProgress: 10, // Start after enhance prompt completes
    maxProgress: 100, // Goes to 100% - this is the main stage
    defaultMessage: 'Generating your video...',
  },
  storing_video_s3: {
    minProgress: 90,
    maxProgress: 95,
    defaultMessage: 'Storing video in S3...',
  },
  storing_video_db: {
    minProgress: 95,
    maxProgress: 100,
    defaultMessage: 'Finalizing...',
  },
  completed: {
    minProgress: 100,
    maxProgress: 100,
    defaultMessage: 'Video ready!',
  },
  failed: {
    minProgress: 0,
    maxProgress: 0,
    defaultMessage: 'Generation failed',
  },
};

/**
 * Calculate overall progress from stage and stage progress
 */
export function calculateOverallProgress(
  stage: GenerationStage,
  stageProgress: number
): number {
  const config = STAGE_CONFIG[stage];
  if (!config) return 0;

  const range = config.maxProgress - config.minProgress;
  const progressInRange = (stageProgress / 100) * range;
  return config.minProgress + progressInRange;
}

/**
 * Get human-readable stage name
 */
export function getStageDisplayName(stage: GenerationStage): string {
  const names: Record<GenerationStage, string> = {
    idle: 'Ready',
    uploading_image: 'Uploading Image',
    storing_image_s3: 'Storing Image',
    storing_image_db: 'Saving Image',
    enhancing_prompt: 'Enhancing Prompt',
    starting_generation: 'Starting Generation',
    generating_video: 'Generating Video',
    storing_video_s3: 'Storing Video',
    storing_video_db: 'Finalizing',
    completed: 'Complete',
    failed: 'Failed',
  };
  return names[stage];
}

